/**
 * Logger Service
 *
 * Servicio centralizado para registro de errores y eventos.
 *
 * SEGURIDAD:
 * - En PRODUCCIÓN: Los logs solo se envían a la consola del navegador (sin persistencia)
 * - En DESARROLLO: Los logs se guardan en localStorage para debugging
 * - NUNCA se almacena información sensible en producción
 */

const LOG_LEVELS = {
  ERROR: 'ERROR',
  WARN: 'WARN',
  INFO: 'INFO',
  DEBUG: 'DEBUG'
};

const MAX_LOGS = 50; // Máximo número de logs en desarrollo
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Lista de campos sensibles que NO deben ser logueados
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'authorization',
  'auth',
  'secret',
  'apiKey',
  'api_key',
  'creditCard',
  'ssn',
  'email', // En producción, ofuscar emails
  'phone'  // En producción, ofuscar teléfonos
];

class Logger {
  constructor() {
    this.logs = IS_PRODUCTION ? [] : this.loadLogs();
    this.setupProductionWarning();
  }

  /**
   * Advertir si se intenta acceder a logs en producción
   */
  setupProductionWarning() {
    if (IS_PRODUCTION && this.logs.length > 0) {
      console.warn(
        '⚠️ SECURITY WARNING: Logs detected in production localStorage. Clearing for security.'
      );
      this.clearLogs();
    }
  }

  /**
   * Sanitizar metadata removiendo información sensible
   */
  sanitizeMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object') {
      return metadata;
    }

    const sanitized = { ...metadata };

    // Remover campos sensibles
    SENSITIVE_FIELDS.forEach((field) => {
      if (field in sanitized) {
        sanitized[field] = IS_PRODUCTION ? '[REDACTED]' : sanitized[field];
      }
    });

    // Sanitizar objetos anidados
    Object.keys(sanitized).forEach((key) => {
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeMetadata(sanitized[key]);
      }
    });

    // En producción, limitar tamaño de metadata
    if (IS_PRODUCTION) {
      const metadataStr = JSON.stringify(sanitized);
      if (metadataStr.length > 1000) {
        return {
          ...sanitized,
          _note: 'Metadata truncated for security',
          _size: metadataStr.length
        };
      }
    }

    return sanitized;
  }

  /**
   * Cargar logs desde localStorage (solo desarrollo)
   */
  static loadLogsFromStorage() {
    if (IS_PRODUCTION) {
      return [];
    }

    try {
      const stored = localStorage.getItem('otx_logs');
      const logs = stored ? JSON.parse(stored) : [];
      // Retornar logs para que puedan ser asignados a this.logs
      return logs;
    } catch (error) {
      console.error('Error loading logs:', error);
      return [];
    }
  }

  // eslint-disable-next-line class-methods-use-this
  loadLogs() {
    return Logger.loadLogsFromStorage();
  }

  /**
   * Guardar logs en localStorage (solo desarrollo)
   */
  saveLogs() {
    if (IS_PRODUCTION) {
      // En producción, NO guardar en localStorage
      return;
    }

    try {
      // Mantener solo los últimos MAX_LOGS
      const logsToSave = this.logs.slice(-MAX_LOGS);
      localStorage.setItem('otx_logs', JSON.stringify(logsToSave));
      this.logs = logsToSave;
    } catch (error) {
      console.error('Error saving logs:', error);
    }
  }

  /**
   * Crear entrada de log
   */
  createLogEntry(level, message, metadata = {}) {
    const sanitizedMetadata = this.sanitizeMetadata(metadata);

    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      metadata: sanitizedMetadata,
      environment: process.env.NODE_ENV
    };

    // En desarrollo, incluir más detalles
    if (!IS_PRODUCTION) {
      logEntry.userAgent = navigator.userAgent;
      logEntry.url = window.location.href;
    }

    return logEntry;
  }

  /**
   * Enviar log a servicio externo (producción)
   * TODO: Implementar integración con servicio de logging (Sentry, LogRocket, etc.)
   */
  static async sendLogToExternalService(logEntry) {
    if (!IS_PRODUCTION) {
      return null; // Solo en producción
    }

    try {
      // TODO: Implementar envío a servicio de logging
      // Ejemplo con Sentry:
      // if (window.Sentry) {
      //   window.Sentry.captureException(new Error(logEntry.message), {
      //     level: logEntry.level.toLowerCase(),
      //     extra: logEntry.metadata
      //   });
      // }

      // Por ahora, solo console en producción
      console.error('[OTRIX Error]:', logEntry.message, logEntry.metadata);
      return true;
    } catch (error) {
      console.error('Failed to send log to external service:', error);
      return false;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async sendToExternalService(logEntry) {
    return Logger.sendLogToExternalService(logEntry);
  }

  /**
   * Registrar error
   */
  error(message, error = null, metadata = {}) {
    const logEntry = this.createLogEntry(LOG_LEVELS.ERROR, message, {
      ...metadata,
      error: error
        ? {
            message: error.message,
            name: error.name,
            // Stack trace solo en desarrollo
            ...(IS_PRODUCTION ? {} : { stack: error.stack })
          }
        : null
    });

    // En desarrollo, guardar en memoria y localStorage
    if (!IS_PRODUCTION) {
      this.logs.push(logEntry);
      this.saveLogs();
      console.error('🔴 ERROR:', message, error, metadata);
    } else {
      // En producción, enviar a servicio externo
      this.sendToExternalService(logEntry);
    }

    return logEntry;
  }

  /**
   * Registrar GraphQL error
   */
  graphQLError(operation, graphQLErrors, networkError = null) {
    const message = 'GraphQL Error';
    const metadata = {
      operation: operation?.operationName || 'Unknown',
      // En producción, NO incluir variables (pueden tener datos sensibles)
      ...(IS_PRODUCTION ? {} : { variables: operation?.variables || {} }),
      graphQLErrors: graphQLErrors?.map((err) => ({
        message: err.message,
        // Extensions solo en desarrollo
        ...(IS_PRODUCTION ? {} : { extensions: err.extensions }),
        path: err.path
      })),
      networkError: networkError
        ? {
            message: networkError.message,
            statusCode: networkError.statusCode
          }
        : null
    };

    return this.error(message, null, metadata);
  }

  /**
   * Registrar advertencia
   */
  warn(message, metadata = {}) {
    const logEntry = this.createLogEntry(LOG_LEVELS.WARN, message, metadata);

    if (!IS_PRODUCTION) {
      this.logs.push(logEntry);
      this.saveLogs();
      console.warn('⚠️ WARNING:', message, metadata);
    } else {
      console.warn('[OTRIX Warning]:', message);
    }

    return logEntry;
  }

  /**
   * Registrar información
   */
  info(message, metadata = {}) {
    const logEntry = this.createLogEntry(LOG_LEVELS.INFO, message, metadata);

    if (!IS_PRODUCTION) {
      this.logs.push(logEntry);
      this.saveLogs();
      console.info('ℹ️ INFO:', message, metadata);
    }

    return logEntry;
  }

  /**
   * Registrar debug (solo desarrollo)
   */
  debug(message, metadata = {}) {
    if (!IS_PRODUCTION) {
      const logEntry = this.createLogEntry(LOG_LEVELS.DEBUG, message, metadata);
      console.log('🐛 DEBUG:', message, metadata);
      return logEntry;
    }
    return null;
  }

  /**
   * Obtener todos los logs (solo desarrollo)
   */
  getLogs(level = null) {
    if (IS_PRODUCTION) {
      console.warn('Logs are not persisted in production for security reasons');
      return [];
    }

    if (level) {
      return this.logs.filter((log) => log.level === level);
    }
    return this.logs;
  }

  /**
   * Limpiar logs
   */
  clearLogs() {
    this.logs = [];
    if (!IS_PRODUCTION) {
      localStorage.removeItem('otx_logs');
    }
  }

  /**
   * Exportar logs como JSON (solo desarrollo)
   */
  exportLogs() {
    if (IS_PRODUCTION) {
      console.warn('Cannot export logs in production');
      return;
    }

    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `otrix-logs-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Obtener estadísticas de errores (para reportes)
   */
  getErrorStats() {
    if (IS_PRODUCTION) {
      return null;
    }

    const errors = this.logs.filter((log) => log.level === 'ERROR');
    return {
      totalErrors: errors.length,
      lastError: errors[errors.length - 1],
      errorsByOperation: errors.reduce((acc, err) => {
        const op = err.metadata?.operation || 'Unknown';
        acc[op] = (acc[op] || 0) + 1;
        return acc;
      }, {})
    };
  }
}

// Exportar instancia singleton
const logger = new Logger();

// Limpiar logs antiguos en producción al cargar
if (IS_PRODUCTION) {
  try {
    localStorage.removeItem('otx_logs');
  } catch (e) {
    // Ignorar errores de acceso a localStorage
  }
}

export default logger;
